/**
 * WebSocket wire-protocol control plane.
 *
 * Per IMPLEMENTATION_PLAN §5.3 + §5.11. Bumped each milestone that changes
 * wire shape:
 *   M1 = 1
 *   M2 = 2 (binary stroke frames)
 *   M3 = 3 (lobby + settings frames)
 *   M4 = 4 (chat + game frames)
 *   v4 T1 = 5 (in-game fill is a real synced ordered op, openStrokes is now
 *             a stroke|fill union; FILL_BROADCAST carries an op id)
 *   v4 T4 = 6 (multi-word skeleton, WORD_HIDDEN + the redacted snapshot
 *             currentTurn carry a `wordPattern` structural string)
 *   v4.2 T10 = 7 (public wrong-guess feed, new CHAT_MESSAGE frame, redacted
 *             per recipient so the drawer's word never leaks)
 *   audit Q9 = 8 (roster truth: new PLAYER_LEFT_BROADCAST frame so a leave
 *             removes the player from every client's roster immediately;
 *             leavers used to ghost until the next snapshot) ← here
 *
 * **Control plane:** JSON, frames below. **Stroke samples:** binary (M2+).
 * Binary frames use the codec in `wire-binary.ts`. The frameType discriminant
 * is the first byte: 0x01=STROKE_START, 0x02=STROKE_BATCH, 0x03=STROKE_END.
 *
 * Version 9 requires the common 1600 by 1200 drawing space.
 * The handshake is HELLO → WELCOME on success, HELLO → PLEASE_RELOAD on
 * protocolVersion mismatch, HELLO → ROOM_GONE when a client tries to resume
 * a room the server no longer knows about (server-restart UX, §5.11).
 */

import type { PlayerId, Point, RoomId, RoomPhase, RoomSettings, WordTier } from "./types.js";
import type { SerializedRoomSnapshot } from "./snapshotWire.js";

// ---------------------------------------------------------------------------
// Serialization helpers (Map/Set are not JSON-serializable; use these instead)
// ---------------------------------------------------------------------------

/**
 * JSON-serializable representation of a player sent in ROOM_STATE_SYNC.
 * Mirrors the Player type from types.ts but uses plain arrays/objects.
 */
export type SerializedPlayer = {
  id: PlayerId;
  name: string;
  /** Hex color: one of the 8 brand colors. */
  color: string;
};

/** Current wire protocol version. Bump on EVERY wire-shape change. */
export const PROTOCOL_VERSION = 9 as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

// ---------------------------------------------------------------------------
// Inbound (client → server) control frames
// ---------------------------------------------------------------------------

/**
 * First frame the client sends on a fresh WS connection. If the server's
 * `protocolVersion` doesn't match, server replies `PLEASE_RELOAD` and closes.
 * If `sessionToken` + `roomId` are present, server attempts resume; if the
 * room no longer exists, server replies `ROOM_GONE`.
 */
export type HelloFrame = {
  kind: "HELLO";
  clientVersion: string;
  protocolVersion: number;
  /** Set on resume attempts (rejoin same session); omitted on first join. */
  sessionToken?: string;
  /** Set on resume attempts; omitted on first join. */
  roomId?: RoomId;
  /**
   * M3.T6: player display name (3-20 chars after NFD normalization + trim).
   * Sent on first HELLO to a room. Stored in the session registry and used
   * as the player's name in ROOM_STATE_SYNC.
   */
  name?: string;
  /**
   * M3.T6: player color (one of the 8 brand hex colors).
   * Sent on first HELLO to a room. Defaults to "#000000" if omitted.
   * T17: the SERVER may reassign this to a random unused color if the pick
   * collides with another current player; the assigned color comes back in
   * the joiner's SerializedPlayer.
   */
  color?: string;
};

// ---------------------------------------------------------------------------
// Outbound (server → client) control frames
// ---------------------------------------------------------------------------

/** Successful handshake. */
export type WelcomeFrame = {
  kind: "WELCOME";
  playerId: PlayerId;
  sessionToken: string;
  serverVersion: string;
  protocolVersion: ProtocolVersion;
};

/** Sent on protocolVersion mismatch. Client shows reload banner. */
export type PleaseReloadFrame = {
  kind: "PLEASE_RELOAD";
  reason: "protocol-version-mismatch";
  serverProtocolVersion: ProtocolVersion;
};

/** Sent when a client resumes a session whose room no longer exists (§5.11). */
export type RoomGoneFrame = {
  kind: "ROOM_GONE";
  roomId: RoomId;
};

// ---------------------------------------------------------------------------
// M2 outbound JSON frames (server → client, non-handshake)
// ---------------------------------------------------------------------------

/**
 * JSON-serializable stroke entry for ROOM_STATE_SYNC.
 * Mirrors OpenStroke but with required path (only finalized strokes are sent).
 */
export type SerializedStroke = {
  strokeId: string;
  drawerId: PlayerId;
  color: string;
  brushSize: number;
  isEraser: boolean;
  /** Final smoothed SVG path from strokeToSvgPath(). */
  path: string;
};

/**
 * Sent to a client on join / reconnect so the audience can render the
 * current canvas state. Carries the full smoothed stroke log. §5.11.
 *
 * `snapshot.state` is now a `SerializedRoomState` (M4 batch 6b, Map/Set
 * fields fully projected to JSON-safe arrays). The top-level side-channel
 * fields (`players`, `spectatorIds`, `serializedStrokes`) are retained for
 * back-compat with the original batch-6a wire, clients can prefer
 * `snapshot.state` going forward.
 *
 * `creatorId` is set for freeDraw rooms; null for game rooms (M3+).
 */
export type RoomStateSyncFrame = {
  kind: "ROOM_STATE_SYNC";
  snapshot: SerializedRoomSnapshot;
  /**
   * @deprecated Read from `snapshot.state.players` instead. Retained for
   * back-compat with batch-6a clients; will be removed in a later batch.
   */
  players: ReadonlyArray<SerializedPlayer>;
  /**
   * Serialized stroke log: JSON-safe representation of openStrokes Map.
   * B3 fix: Maps serialize to `{}` in JSON; this field carries the actual data.
   * Only strokes with a path (finalized via STROKE_END) are included.
   */
  serializedStrokes: ReadonlyArray<SerializedStroke>;
  /** Creator of a freeDraw room; null for game rooms. */
  creatorId: PlayerId | null;
  /**
   * M3.T13: Player IDs in the spectators set (joined mid-game).
   * JSON-safe array (ReadonlySet is not JSON-serializable).
   */
  spectatorIds: ReadonlyArray<PlayerId>;
  /**
   * v4.2 T10: bounded recent public chat backlog for late joiners. ALREADY
   * REDACTED for `playerId` by the server (the drawer's copy omits every
   * `miss` line; `correct` lines never carry the word). Absent / empty when
   * there's no game chat yet. Oldest → newest order.
   */
  chatLog?: ReadonlyArray<SerializedChatMessage>;
};

/**
 * Sent to a client whose stroke or control action was rejected for authz
 * reasons (§13.2.b). Client shows an error toast.
 */
export type AuthRejectFrame = {
  kind: "AUTH_REJECT";
  reason: string;
};

/**
 * Broadcast to all room members when the drawer performs a fill operation.
 * v4 T1 (D5): a fill is now an ordered op in `openStrokes` and is broadcast in
 * every mode (the §5.9 free-draw-only gate is lifted). JSON control frame; the
 * client replays the fill on its canvas upon receiving this frame. `strokeId`
 * is the op id so the drawer dedups its own echo against the optimistic local
 * append (mirrors the strokeId-dedup on STROKE_END).
 */
export type FillBroadcastFrame = {
  kind: "FILL_BROADCAST";
  strokeId: string;
  point: { x: number; y: number };
  color: string; // BrandColor hex
};

/**
 * Broadcast to all room members when the drawer undoes their last stroke.
 * Audience clients drop the matching strokeId from their local stroke log
 * and repaint. Sent to every recipient role, canvas state is public.
 */
export type StrokeUndoBroadcastFrame = {
  kind: "STROKE_UNDO_BROADCAST";
  strokeId: string;
};

/**
 * Broadcast to all room members when the drawer clears the canvas.
 * Audience clients empty their local stroke log + wipe the canvas. Sent to
 * every recipient role.
 */
export type StrokeClearBroadcastFrame = {
  kind: "STROKE_CLEAR_BROADCAST";
};

// ---------------------------------------------------------------------------
// M3 outbound JSON frames: state-change broadcasts (B6 fix).
// ---------------------------------------------------------------------------

/**
 * Sent to all room members when a player joins the lobby. Carries the new
 * player record so clients can append without waiting for the next
 * ROOM_STATE_SYNC. M3.fix B6.
 */
export type PlayerJoinBroadcastFrame = {
  kind: "PLAYER_JOIN_BROADCAST";
  player: {
    id: PlayerId;
    name: string;
    /** Hex; one of the 8 brand colors. */
    color: string;
  };
};

/**
 * Sent to all room members when the host updates settings. M3.fix B6.
 */
export type SettingsUpdatedFrame = {
  kind: "SETTINGS_UPDATED";
  settings: RoomSettings;
};

/**
 * Sent to all room members when the host migrates (e.g. host disconnect).
 * M3.fix B6.
 */
export type HostChangedFrame = {
  kind: "HOST_CHANGED";
  newHostId: PlayerId;
};

/**
 * Sent to all room members when the game phase transitions. M3.fix B6.
 * Reason is a stable string key from the §13.4 triggers list (e.g.
 * "start-game", "draw-timer-expired", "all-guessed").
 */
export type PhaseChangedFrame = {
  kind: "PHASE_CHANGED";
  prevPhase: RoomPhase;
  nextPhase: RoomPhase;
  reason: string;
};

/**
 * Sent to all room members when a player is kicked. The kicked player's
 * own WS is closed with code 1008, others get this frame. M3.fix B6.
 */
export type PlayerKickedFrame = {
  kind: "PLAYER_KICKED";
  playerId: PlayerId;
};

/**
 * Sent to all room members when spectators are promoted at a round
 * boundary (§5.8). M3.fix B6.
 */
export type SpectatorsPromotedFrame = {
  kind: "SPECTATORS_PROMOTED";
  promotedIds: ReadonlyArray<PlayerId>;
};

/**
 * Sent to all remaining room members when a player leaves (socket close →
 * PLAYER_LEAVE). Protocol 8 (audit Q9): clients remove the player from
 * their roster immediately: leavers used to ghost in every roster until
 * the next ROOM_STATE_SYNC (in the lobby that meant indefinitely, which is
 * how a host ended up unable to start a "2-player" room with one real
 * player). The PLAYER_KICKED twin for voluntary/disconnect departures;
 * PLAYER_LEAVE_NOTICE stays the separate, suppressible toast.
 */
export type PlayerLeftBroadcastFrame = {
  kind: "PLAYER_LEFT_BROADCAST";
  playerId: PlayerId;
};

// ---------------------------------------------------------------------------
// M4 outbound JSON frames: game-loop fanout with role-based redaction.
// ---------------------------------------------------------------------------

/**
 * Sent to recipients who are allowed to see the drawer's word verbatim:
 * the drawer themselves and any `audience-correct` recipients (they
 * already guessed correctly this turn, so revealing the word leaks
 * nothing). The wire layer picks WORD_PICKED vs WORD_HIDDEN per recipient
 * per §5.12 chat redaction.
 */
export type WordPickedFrame = {
  kind: "WORD_PICKED";
  drawerId: PlayerId;
  /** Canonical drawer-visible word. */
  word: string;
  tier: WordTier;
};

/**
 * Sent to recipients who must NOT see the drawer's word: `audience-pending`
 * (haven't guessed correctly yet) and `spectator` (mid-game joiners). Carries
 * the word's length AND a redaction-safe structural `wordPattern` so the
 * client can render a multi-word placeholder (e.g. "___ _____") without
 * leaking letters (v4 T4).
 */
export type WordHiddenFrame = {
  kind: "WORD_HIDDEN";
  drawerId: PlayerId;
  /** Length of the drawer's word (count of code units). */
  wordLength: number;
  /**
   * Structural skeleton of the word: every letter/digit → "_", spaces +
   * punctuation preserved verbatim (v4 T4, see `redactWordPattern`). Lets
   * the audience render word breaks ("___ _____") without seeing letters.
   * Always `wordPattern.length === wordLength`.
   */
  wordPattern: string;
  tier: WordTier;
};

/**
 * Broadcast to all room members when an audience member's guess is an
 * exact match. Carries `order` (1-indexed correct-guesser slot) + `points`
 * so the client can render the toast + scoreboard increment without
 * recomputing the scoring formula.
 */
export type GuessCorrectFrame = {
  kind: "GUESS_CORRECT";
  playerId: PlayerId;
  order: number;
  points: number;
};

/**
 * Whispered close-call: routed ONLY back to the guesser (per §5.12). The
 * non-guesser wire never carries the text. Lets the guesser see "you're
 * close!" without leaking the near-match to the rest of the room.
 */
export type GuessCloseFrame = {
  kind: "GUESS_CLOSE";
  playerId: PlayerId;
  /** Guesser-visible text (already echoed in their own chat). */
  text: string;
};

/**
 * Whispered miss: routed ONLY back to the guesser. Lets the guesser's chat
 * pane show their own messages without leaking them to other audience
 * members (or the drawer) per §5.12.
 */
export type GuessMissFrame = {
  kind: "GUESS_MISS";
  playerId: PlayerId;
  text: string;
};

/**
 * Public chat-feed line for the wrong-guess feed (v4.2 T10, §5.12). The wire
 * layer routes this per recipient so the drawer's word never leaks:
 *
 *   - `chatKind: "miss"` carries the guesser's SAFE wrong-guess `text` (already
 *     cleared by `isSafePublicGuess` in the reducer). `eventsToWire` broadcasts
 *     it to the whole room EXCEPT the drawer (the drawer receives ZERO chat
 *     text). Spectators DO receive it (it's other players' wrong guesses).
 *   - `chatKind: "correct"` carries NO `text`, only `name`/`color`. It
 *     announces "X guessed it!" to everyone including the drawer; safe because
 *     it never contains the word.
 *
 * `color` is the guesser's BRAND_COLOR (the feed renders the author name in it,
 * §C9). `playerId` lets the client dedup against its own optimistic echoes.
 */
export type ChatMessageFrame = {
  kind: "CHAT_MESSAGE";
  playerId: PlayerId;
  name: string;
  /** Guesser's brand hex color: used to tint the author name in the feed. */
  color: string;
  chatKind: "miss" | "correct";
  /** Present for `miss` (the safe wrong-guess text); absent for `correct`. */
  text?: string;
};

/**
 * One entry in the late-joiner chat backlog (v4.2 T10). Mirrors the payload
 * fields of `ChatMessageFrame` minus the `kind` discriminant. The server keeps
 * a BOUNDED recent slice and ships it on `ROOM_STATE_SYNC`, REDACTED PER
 * RECIPIENT (the drawer never receives `miss` lines, see snapshotRedaction).
 * `id` is a monotonic per-room sequence so the client can dedup the backlog
 * against any live CHAT_MESSAGE frames that raced the snapshot.
 */
export type SerializedChatMessage = {
  id: number;
  playerId: PlayerId;
  name: string;
  color: string;
  chatKind: "miss" | "correct";
  text?: string;
};

/**
 * Broadcast to all room members when the last audience member has just
 * guessed correctly. Hints the client to swap to a "wrapping up" state
 * while the server early-ends the draw timer (PHASE_END(drawing→reveal)
 * with reason="all-guessed" follows shortly).
 */
export type AllGuessedFrame = {
  kind: "ALL_GUESSED";
};

/**
 * Broadcast to all room members when an audience reaction passes the
 * server-side rate limit gate. Visual layer only (PRD §3.3). Position is
 * client-supplied; server-stamped `t` orders overlay frames across clients.
 */
export type ReactionFrame = {
  kind: "REACTION";
  playerId: PlayerId;
  emoji: string;
  pos: Point;
  t: number;
};

/**
 * Broadcast to all room members when the active drawer's socket drops
 * and the 10s grace window begins (PRD §3.8). Carries `graceUntil` so
 * clients render a countdown without an additional round-trip.
 */
export type DrawerDisconnectGraceFrame = {
  kind: "DRAWER_DISCONNECT_GRACE";
  playerId: PlayerId;
  graceUntil: number;
};

/**
 * Broadcast to all room members when the drawer reconnects before the
 * 10s grace window expires. Clears the "reconnecting…" UI.
 */
export type DrawerReconnectFrame = {
  kind: "DRAWER_RECONNECT";
  playerId: PlayerId;
};

/**
 * Hint reveal broadcast (M4 batch 5, PRD §3.5). Carries the cumulative
 * set of letter positions the server has decided to reveal so far. The
 * wire layer routes this to `audience-pending` recipients ONLY, the
 * drawer already sees the full word; audience-correct recipients have
 * already guessed; spectators get no hints (per §5.12 chat redaction
 * principle: hints leak information that should stay scoped to active
 * guessers).
 *
 * Indices are serialized as an array (Set is not JSON-safe). Order is
 * does not matter; clients render letter-by-letter regardless.
 *
 * **M4 batch 6c, letter pairs.** Also carries `revealedLetters`: the
 * `[{index, char}, ...]` projection of `revealedIndices` against the
 * canonical word. The audience-pending recipient never sees the word
 * directly (snapshot redactor blanks it to `""`), so the letter
 * pairs are the *only* path for them to render "_ _ _ c _ e _ _ _"
 * with actual characters. Sorted by `index` ascending so the wire
 * shape is deterministic.
 */
export type HintRevealedFrame = {
  kind: "HINT_REVEALED";
  revealedIndices: ReadonlyArray<number>;
  /** Index + character pairs at the revealed positions, sorted by index ascending. */
  revealedLetters: ReadonlyArray<{ index: number; char: string }>;
};

/**
 * Broadcast to all room members (no redaction, `playerId` + `displayName`
 * are public roster info) when an audience-or-spectator player leaves an
 * active game phase. M4 batch 5.
 *
 * Drawer disconnects are NOT routed through this frame, they fire the
 * dedicated `DRAWER_DISCONNECT_GRACE` lifecycle instead (PRD §3.8). The
 * reducer's `PLAYER_LEAVE` handler suppresses the notice for the active
 * drawer to avoid double-broadcast.
 *
 * `reason` is open-ended for future "voluntary leave" telemetry; the only
 * source today is socket close, so the server always sends `"disconnect"`.
 */
export type PlayerLeaveNoticeFrame = {
  kind: "PLAYER_LEAVE_NOTICE";
  playerId: PlayerId;
  displayName: string;
  reason: "disconnect" | "leave";
};

// ---------------------------------------------------------------------------
// Unions
// ---------------------------------------------------------------------------

export type InboundControlFrame = HelloFrame;

export type OutboundControlFrame =
  | WelcomeFrame
  | PleaseReloadFrame
  | RoomGoneFrame
  | RoomStateSyncFrame
  | AuthRejectFrame
  | FillBroadcastFrame
  | StrokeUndoBroadcastFrame
  | StrokeClearBroadcastFrame
  // M3.fix B6: state-change broadcasts.
  | PlayerJoinBroadcastFrame
  | SettingsUpdatedFrame
  | HostChangedFrame
  | PhaseChangedFrame
  | PlayerKickedFrame
  | SpectatorsPromotedFrame
  // Protocol 8 (audit Q9): roster truth on leave.
  | PlayerLeftBroadcastFrame
  // M4: game-loop fanout.
  | WordPickedFrame
  | WordHiddenFrame
  | GuessCorrectFrame
  | GuessCloseFrame
  | GuessMissFrame
  | ChatMessageFrame
  | AllGuessedFrame
  | ReactionFrame
  | DrawerDisconnectGraceFrame
  | DrawerReconnectFrame
  | PlayerLeaveNoticeFrame
  | HintRevealedFrame;

/**
 * WireFrame: M2 now includes binary stroke frames alongside JSON control
 * frames. The `BinaryStrokeFrame` variant represents any of the three binary
 * stroke messages (STROKE_START / STROKE_BATCH / STROKE_END). The actual
 * binary buffer is carried by `BinaryStrokeFrame.buf`. Use `parseStrokeFrame`
 * from `wire-binary.ts` to decode the buffer.
 *
 * eventsToWire produces WireFrame[] so transport can send JSON vs. binary as
 * appropriate based on `kind === "BINARY_STROKE"`.
 */
export type BinaryStrokeFrame = {
  kind: "BINARY_STROKE";
  buf: ArrayBuffer;
};

export type WireFrame = OutboundControlFrame | BinaryStrokeFrame;
